import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { useSession } from '../lib/session';
import { Alert, Button, Field, Input, PasswordInput } from '../components/ui';
import { useT } from '../i18n/locale';

export default function LoginScreen({ mode }: { mode: 'login' | 'signup' }) {
  const { login, refresh } = useSession();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isSignup = mode === 'signup';

  // The same component backs both /login and /signup, so navigating between
  // them keeps its state — including a stale error. Clear it on the switch, so
  // "Email atau password salah." from a failed sign-in does not greet someone
  // who has just moved to the registration form.
  useEffect(() => {
    setError(null);
  }, [mode]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isSignup) {
        await api.post('/signup', { companyName, name, email, password });
        await refresh();
        // Signup no longer grants a trial; the app stays closed until the
        // address is verified, so land them on that screen.
        navigate('/verify');
        return;
      } else {
        await login(email, password);
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.unknownError'));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="m-0 text-2xl font-black text-white">DR Inbound</h1>
          <p className="mt-1 text-sm text-white/60">
            {t('login.tagline')}
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl"
        >
          <h2 className="m-0 text-lg font-bold text-slate-800">
            {isSignup ? t('login.signupTitle') : t('login.title')}
          </h2>

          {error ? <Alert tone="error">{error}</Alert> : null}

          {isSignup ? (
            <>
              <Field label={t('signup.company')}>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  autoFocus
                  placeholder={t('signup.companyPlaceholder')}
                />
              </Field>
              <Field label={t('signup.name')}>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>
            </>
          ) : null}

          <Field label={t('login.email')}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus={!isSignup}
              autoComplete="username"
            />
          </Field>

          <Field
            label={t('login.password')}
            hint={
              isSignup ? t('signup.passwordHint') : undefined
            }
          >
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isSignup ? 'new-password' : 'current-password'}
            />
          </Field>

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? t('login.submitting') : isSignup ? t('signup.submit') : t('login.submit')}
          </Button>

          {!isSignup ? (
            <p className="m-0 text-center">
              <Link
                to="/forgot-password"
                className="text-xs font-semibold text-brand-500 no-underline hover:underline"
              >
                {t('login.forgotLink')}
              </Link>
            </p>
          ) : null}

          <p className="m-0 text-center text-xs text-slate-500">
            {isSignup ? (
              <>
                {t('login.haveAccount')}{' '}
                <Link to="/login" className="font-bold text-brand-500">
                  {t('login.loginLink')}
                </Link>
              </>
            ) : (
              <>
                {t('login.noAccount')}{' '}
                <Link to="/signup" className="font-bold text-brand-500">
                  {t('login.signupLink')}
                </Link>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
